<template>
  <div>
    <div v-for='(term, index) in terms'
      :key='`term-${index}`'
      class='text-xs border rounded-md mb-8'>
      <div class='bg-gray-100 p-4' v-html='term.text'></div>
      <label class='border-t p-4 flex flex-row justify-start items-center gap-x-0.5 text-normal'>
        <input type='checkbox' :name='`checkbox-${index}`' v-model='term.agreed'>
        <span class='px-1'>{{ term.confirmationText }}</span>
      </label>
    </div>
    <form-field-medical-profession
      v-bind:medical-profession.sync='extraInfo.medicalProfession'
      class='mb-6' />
    <form-field-title v-if='!isKorean'
      v-bind:title.sync='title'
      class='mb-6' />
    <div class='mb-6'>
      <label class='uppercase text-sm text-gray-500'>First & Middle Name (English)</label>
      <input
        v-model='firstName'
        class='border py-1 px-2 rounded block w-full lg:w-80'
        type='text'
        @input='blockKoreanInput($event)'
        placeholder='First & Middle Name'>
    </div>
    <div class='mb-6'>
      <label class='uppercase text-sm text-gray-500'>Last Name (English)</label>
      <input
        v-model='lastName'
        class='border py-1 px-2 rounded block w-full lg:w-80'
        type='text'
        @input='blockKoreanInput($event)'
        placeholder='Last Name'>
    </div>
    <div class='mb-6' v-if='isKorean'>
      <label class='uppercase text-sm text-gray-500 mb-2'>성명 (국문)</label>
      <input
        v-model='name'
        class='border py-1 px-2 rounded block w-full lg:w-80'
        type='text'
        @input='onlyKoreanNameInput($event)'
        placeholder='성명'>
    </div>
    <div class='mb-6' v-if='isKorean'>
      <label class='uppercase text-sm text-gray-500 mb-2'>License Number</label>
      <input
        v-model='licenseNumber'
        class='border py-1 px-2 rounded block w-full lg:w-80'
        type='text'
        placeholder='License Number'>
    </div>
    <div class='mb-6' v-if='isKorean'>
      <label class='uppercase text-sm text-gray-500 mb-2'
        :style='ageTypeStringStyle'>
        {{ ageTypeString }}
      </label>
      <button class='border py-1 px-2 rounded block w-full lg:w-80 text-white uppercase '
      :style='`background-color: ${eventMainThemeColor};`'
        @click='checkAgeType'>
        만65세 이상회원 또는 수연회원 인증하기
      </button>
      <div class='text-gray-500 text-xs leading-4 mt-2 w-full lg:w-80'>만65세 기준연도: 1957년부터 이전 출생자<br>
        수연회원 기준: 1961년 10월 17일부터 1962년 10월 15일 출생자
      </div>
    </div>
    <form-field-affiliation
      v-bind:hospitalName.sync='hospitalName'
      v-bind:organization.sync='organization'
      :is-korean='isKorean' />
    <form-field-department
      v-bind:department.sync='department'
      class='mb-6' />
    <div class='mb-6'>
      <label class='uppercase text-sm text-gray-500 mb-2'>Participation Type (English)</label>
      <select
        v-model='participationType'
        class='border py-1 px-2 rounded block w-full lg:w-80'>
        <option disabled></option>
        <option v-for='participationType in participationTypes'
          :key='`participationType-${participationType.value}`'
          :value='participationType.value'>
          {{participationType.fullDescription}}
        </option>
      </select>
      <div class='text-gray-500 text-xs leading-4 mt-2 w-full lg:w-80'>Online only participation registration is available.</div>
    </div>
    <div class='mb-6'>
      <label class='uppercase text-sm text-gray-500 mb-2'>Participant Type (English)</label>
      <select
        v-model='participantType'
        class='border py-1 px-2 rounded block w-full lg:w-80'>
        <option disabled></option>
        <option v-for='participantType in participantTypes'
          :key='`participantType-${participantType.value}`'
          :value='participantType.value'>
          {{participantType.fullDescription}}
        </option>
      </select>
    </div>
    <div class='mb-6' v-if='isKorean && isParticipationTypeOffline'>
      <label class='uppercase text-sm text-gray-500 mb-2'>명찰수령방식 (국문)</label>
      <select
        v-model='nametagPickupLocation'
        class='border py-1 px-2 rounded block w-full lg:w-80'>
        <option disabled></option>
        <option v-for='location in nametagPickupLocations' 
          :key='`location-${location.value}`'
          :value='location.value'>
          {{location.fullDescription}}
        </option>
      </select>
    </div>
    <div class='mb-6' v-if='isKorean && isParticipationTypeOffline && isNametagPickupLocationTypeHome'>
      <label class='uppercase text-sm text-gray-500 mb-2'>명찰수령주소 입력 (국문)</label>
      <button class='input-address-button block'
              @click='openAddressMap'>
        {{ addressString }}
      </button>
      <vue-daum-postcode v-if='showAddressMap'
                         @complete='completeAddressMap'
                         class='border rounded-md mt-2 overflow-y-scroll'
                         style='max-height: 400px'/>
    </div>
    <div class='mb-6' v-if='isKorean && isParticipationTypeOffline && isNametagPickupLocationTypeHome'>
      <label class='uppercase text-sm text-gray-500 mb-2'>명찰수령상세주소 입력 (국문)</label>
      <input
        v-model='addressDetail'
        class='border py-1 px-2 rounded block w-full lg:w-80'
        type='text'
        placeholder='명찰수령상세주소 입력 (국문)'>
    </div>
    <div class='mb-6'>
      <label class='uppercase text-sm text-gray-500 mb-2'>Mobile Phone</label>
      <vue-phone-number-input
        class='block w-full lg:w-80'
        v-model='phoneNumber'
        :default-country-code='defaultCountryCode'
        :valid-color='eventMainThemeColor'
        @update='formatProfilePhoneNumber' />
      <div class='text-gray-500 text-xs leading-4 mt-2 w-full lg:w-80'>Please ensure you have entered your number correctly. <br>It will be used to communicate with you important details about the conference.</div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import SignupTermsHelpers         from '@/utils/signup-terms-helpers.js'
import UserDataHelpers            from '@/utils/user-data-helpers.js'
import FormFieldMedicalProfession from '@/components/FormFieldMedicalProfession.vue'
import FormFieldTitle             from '@/components/FormFieldTitle.vue'
import FormFieldDepartment        from '@/components/FormFieldDepartment.vue'
import FormFieldAffiliation       from '@/components/FormFieldAffiliation.vue'


export default {
  name: 'RegistrationFormFields',
  components: {
    VuePhoneNumberInput,
    FormFieldMedicalProfession,
    FormFieldTitle,
    FormFieldDepartment,
    FormFieldAffiliation,
  },
  data () {
    return {
      displayCountry: '',
      showAddressMap: false,
    }
  },
  watch: {
    'participationType': {
      handler: function (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          let index = this.participationTypes.map((item) => item.value.toLowerCase()).indexOf(newVal.toLowerCase())
          if (index > -1) {
            this.participationType = this.participationTypes[index].value
          } else {
            this.participantType = newVal
          }
        }
      },
      immediate: true 
    },
    'participantType': {
      handler: function (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          let index = this.participantTypes.map((item) => item.value.toLowerCase()).indexOf(newVal.toLowerCase())
          if (index > -1) {
            this.participantType = this.participantTypes[index].value
          } else {
            this.participantType = newVal
          }
        }
      },
      immediate: true 
    },
    'fullAddressString': {
      handler: function (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.nametagAddress = newVal
        }
      },
      immediate: true 
    },
    'showingEventId': {
      handler: function (newVal, oldVal) {
        if (newVal && newVal !== oldVal && this.terms.length === 0) {
          // signup 할 때 terms 없으면, like koa sso login or import data from excel
          this.terms = SignupTermsHelpers.signupTerms(newVal)
        }
      },
      immediate: true 
    },
  },
  computed: {
    ...mapFields('users', [
      'signupParams',
      'profile.firstName',
      'profile.lastName',
      'profile.name',
      'profile.licenseNumber',
      'profile.phoneNumber',
      'profile.title',
      'profile.address',
      'profile.addressDetail',
      'profile.postalCode',
      'profile.department',
      'profile.hospitalName',
      'profile.organization',
      'profile.extraInfo',
      'membershipData.ageType',
      'registrationData.nametagPickupLocation',
      'registrationData.nametagAddress',
      'registrationData.participationType',
      'registrationData.participantType',
      'terms',
    ]),
    ...mapState('users', [
      'profile',
    ]),
    ...mapGetters('events', [
      'eventMainThemeColor',
      'showingEventId',
    ]),
    participantTypes () {
      return UserDataHelpers.selectableValues(this.showingEventId, 'participantTypes')
    },
    participationTypes () {
      return UserDataHelpers.selectableValues(this.showingEventId, 'participationTypes')
    },
    nametagPickupLocations () {
      return UserDataHelpers.selectableValues(this.showingEventId, 'nametagPickupLocation')
    },
    isKorean () {
      return !this.profile.country ||
             this.profile.country.toLowerCase() === 'kr'
    },
    defaultCountryCode () {
      return this.profile.country.toUpperCase()
    },
    addressString () {
      return (this.address) ? this.address : '주소 검색'
    },
    isParticipationTypeOffline () {
      return this.participationType.toLowerCase() === 'offline'
    },
    isNametagPickupLocationTypeHome () {
      return this.nametagPickupLocation === 'home'
    },
    ageTypeParams () {
      return {
        name: this.name.replace(/\s/g, ''),
        licenseNumber: this.licenseNumber.replace(/\s/g, '')
      }
    },
    ageTypeString () {
      if (!this.ageType) {
        return ''
      } else if (this.ageType === 'over65') {
        return '만65세 이상회원 인증되었습니다.'
      } else if (this.ageType === 'trainee') {
        return '수연회원 인증되었습니다.'
      } else {
        return '만65세 이상회원 또는 수연회원이 아닙니다.'
      }
    },
    ageTypeStringStyle () {
      return (this.ageType === 'over65' || this.ageType === 'trainee') ? `color: ${this.eventMainThemeColor}; font-weight: 600;`: ''
    },
    fullAddressString () {
      return `${this.address} ${this.addressDetail}`
    },
  },
  methods: {
    ...mapActions('users',[
      'getAgeType',
    ]),
    openAddressMap () {
      this.showAddressMap = true
    },
    completeAddressMap (event) {
      this.address = event.address
      this.postalCode = event.zonecode
      this.showAddressMap = false
    },
    formatProfilePhoneNumber (data) {
      this.phoneNumber = data.formatInternational
      this.$emit('update-phone-number-is-valid', data.isValid)
    },
    blockKoreanInput(event)  {
      event.target.value = event.target.value.replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/ig, '')
    },
    onlyKoreanNameInput(event) {
      this.name = event.target.value.replace(/[^\u3131-\u3163 ^\uac00-\ud7a3]/g, '')
    },
    checkAgeType () {
      this.getAgeType(this.ageTypeParams).then((resp) => {
        this.ageType = resp
      })
    },
  },
}
</script>

<style type='scss' scoped>
.input-address-button {
  @apply border rounded-md py-1 w-full lg:w-80;
}

.input-address-button:hover {
  @apply bg-blue-800 text-white;
}
</style>
